/**
 * financial.calculator.ts - Finanční výpočty pro TPM Analytics
 * 
 * Centralizace všech finančních vzorců na jednom místě.
 * Důvod oddělení: zjednodušení služeb, centralizace vzorců a snadné testování.
 * 
 * TYPY NÁKLADŮ:
 * 1. Náklady na prostoj (downtime costs) - přímé náklady za prostoj stroje
 * 2. Ušlý zisk (lost profit) - zisk, který jsme nemuseli vydělat kvůli prostoji
 * 3. Náklady na údržbu (maintenance costs) - náklady na techniky
 * 4. Náklady na zmetek (scrap costs) - hodnota zmetků
 */

/**
 * Cache pro předpočítané hodinové sazby pracovišť.
 * Klíč: `${workplaceId}_${fieldName}`
 */
interface WorkplaceRatesCache {
    downtimeCosts: Map<string, number>;
    lostProfit: Map<string, number>;
    maintenanceCosts: Map<string, number>;
}

let currentCache: WorkplaceRatesCache | null = null;

/**
 * Rekurzivně hledá hodinovou sazbu v hierarchii pracoviště → lokace → nadřazená lokace.
 * 
 * @param workplace Workplace objekt
 * @param fieldName Název pole (hourlyDowntimeCosts, hourlyDowntimeLostProfit, hourlyMaintenanceCosts)
 * @returns Nalezená hodnota nebo 0
 */
function findHourlyRateInHierarchy(workplace: any, fieldName: string): number {
    // 1. Zkusit přímo na pracoviště
    const workplaceValue = Number(workplace?.[fieldName] ?? null);
    if (workplaceValue !== null && workplaceValue !== undefined && workplaceValue > 0) {
        return workplaceValue;
    }
    
    // 2. Zkusit lokaci pracoviště (rekurzivně)
    const location = workplace?.location;
    if (location) {
        return findHourlyRateInLocation(location, fieldName);
    }
    
    return 0;
}

/**
 * Rekurzivně hledá hodinovou sazbu v hierarchii lokací.
 * 
 * @param location Location objekt
 * @param fieldName Název pole
 * @returns Nalezená hodnota nebo 0
 */
function findHourlyRateInLocation(location: any, fieldName: string): number {
    if (!location) return 0;
    
    // Zkusit aktuální lokaci
    const locationValue = Number(location?.[fieldName] ?? null);
    if (locationValue !== null && locationValue !== undefined && locationValue > 0) {
        return locationValue;
    }
    
    // Zkusit nadřazenou lokaci (rekurzivně)
    const parentLocation = location?.parentLocation;
    if (parentLocation) {
        return findHourlyRateInLocation(parentLocation, fieldName);
    }
    
    return 0;
}

/**
 * Předpočítá hodinové sazby pro všechna pracoviště.
 * Toto by mělo být zavoláno JEDNOU na začátku výpočtů pro výrazné zvýšení výkonu.
 * 
 * @param workplaces Pole Workplace objektů s location a parentLocation daty
 * @returns Cache objekt s předpočítanými hodnotami
 */
export function buildWorkplaceRatesCache(workplaces: any[]): WorkplaceRatesCache {
    const cache: WorkplaceRatesCache = {
        downtimeCosts: new Map(),
        lostProfit: new Map(),
        maintenanceCosts: new Map(),
    };
    
    // Projdeme všechna pracoviště a vypočítáme jejich sazby
    for (const workplace of workplaces) {
        if (!workplace) continue;
        
        const workplaceId = workplace.id;
        if (!workplaceId) continue;
        
        // Předpočítáme všechny tři sazby pro toto pracoviště
        cache.downtimeCosts.set(
            workplaceId,
            findHourlyRateInHierarchy(workplace, 'hourlyDowntimeCosts')
        );
        cache.lostProfit.set(
            workplaceId,
            findHourlyRateInHierarchy(workplace, 'hourlyDowntimeLostProfit')
        );
        cache.maintenanceCosts.set(
            workplaceId,
            findHourlyRateInHierarchy(workplace, 'hourlyMaintenanceCosts')
        );
    }
    
    return cache;
}

/**
 * Nastaví globální cache pro hodinové sazby.
 * 
 * @param cache Cache objekt vytvořený pomocí buildWorkplaceRatesCache
 */
export function setWorkplaceRatesCache(cache: WorkplaceRatesCache | null): void {
    currentCache = cache;
}

/**
 * Získá hodinovou sazbu z cache nebo ji vypočítá za běhu.
 * 
 * @param planItem PlanItem objekt
 * @param workplaceId ID pracoviště
 * @param cacheMap Mapa z cache pro daný typ sazby
 * @param fieldName Název pole pro fallback výpočet
 * @returns Hodinová sazba
 */
function getHourlyRateFromCacheOrCompute(
    planItem: any,
    workplaceId: string | null,
    cacheMap: Map<string, number>,
    fieldName: string
): number {
    // 1. Zkusit přímo na PlanItem
    const itemValue = Number(planItem?.[fieldName] ?? null);
    if (itemValue !== null && itemValue !== undefined && itemValue > 0) {
        return itemValue;
    }
    
    // 2. Zkusit cache (pokud existuje a máme workplaceId)
    if (currentCache && workplaceId && cacheMap.has(workplaceId)) {
        return cacheMap.get(workplaceId)!;
    }
    
    // 3. Fallback - vypočítat za běhu (pro případy kdy cache není k dispozici)
    const workplace = planItem?.workplace;
    if (workplace) {
        return findHourlyRateInHierarchy(workplace, fieldName);
    }
    
    return 0;
}

/**
 * Získá hodinovou sazbu nákladů na prostoj z PlanItem nebo cache/hierarchie pracoviště/lokací.
 * Pokud je cache aktivní, použije se pro rychlejší lookup.
 * 
 * @param planItem PlanItem objekt
 * @returns Hodinová sazba nákladů na prostoj
 */
export function getHourlyDowntimeCosts(planItem: any): number {
    const workplaceId = planItem?.workplaceId ?? planItem?.workplace?.id ?? null;
    return getHourlyRateFromCacheOrCompute(
        planItem,
        workplaceId,
        currentCache?.downtimeCosts ?? new Map(),
        'hourlyDowntimeCosts'
    );
}

/**
 * Získá hodinovou sazbu ušlého zisku z PlanItem nebo cache/hierarchie pracoviště/lokací.
 * Pokud je cache aktivní, použije se pro rychlejší lookup.
 * 
 * @param planItem PlanItem objekt
 * @returns Hodinová sazba ušlého zisku
 */
export function getHourlyDowntimeLostProfit(planItem: any): number {
    const workplaceId = planItem?.workplaceId ?? planItem?.workplace?.id ?? null;
    return getHourlyRateFromCacheOrCompute(
        planItem,
        workplaceId,
        currentCache?.lostProfit ?? new Map(),
        'hourlyDowntimeLostProfit'
    );
}

/**
 * Získá hodinovou sazbu nákladů na údržbu z PlanItem nebo cache/hierarchie pracoviště/lokací.
 * Pokud je cache aktivní, použije se pro rychlejší lookup.
 * 
 * @param planItem PlanItem objekt
 * @returns Hodinová sazba nákladů na údržbu
 */
export function getHourlyMaintenanceCosts(planItem: any): number {
    const workplaceId = planItem?.workplaceId ?? planItem?.workplace?.id ?? null;
    return getHourlyRateFromCacheOrCompute(
        planItem,
        workplaceId,
        currentCache?.maintenanceCosts ?? new Map(),
        'hourlyMaintenanceCosts'
    );
}

/**
 * Vypočítá náklady na prostoj pro odstávku (PlanItem).
 * 
 * Vzorec: (hodinová sazba / 60) * minuty prostoje
 * 
 * Hierarchie: PlanItem -> Workplace -> Location -> ParentLocation (rekurzivně)
 * 
 * @param item PlanItem s shutdownDuration a hourlyDowntimeCosts
 * @returns Náklady v Kč
 */
export function calcPlanItemDowntimeCostKc(item: any): number {
    const minutes = Number(item?.shutdownDuration ?? 0) || 0;
    const hourly = getHourlyDowntimeCosts(item);
    return (hourly / 60) * minutes;
}

/**
 * Vypočítá ušlý zisk pro odstávku (PlanItem).
 * 
 * Ušlý zisk = zisk, který bychom vydělali, kdyby stroj běžel.
 * Vzorec: (hodinová sazba ušlého zisku / 60) * minuty prostoje
 * 
 * Hierarchie: PlanItem -> Workplace -> Location -> ParentLocation (rekurzivně)
 * 
 * @param item PlanItem s shutdownDuration a hourlyDowntimeLostProfit
 * @returns Ušlý zisk v Kč
 */
export function calcPlanItemLostProfitKc(item: any): number {
    const minutes = Number(item?.shutdownDuration ?? 0) || 0;
    const hourly = getHourlyDowntimeLostProfit(item);
    return (hourly / 60) * minutes;
}

/**
 * Vypočítá náklady na údržbu a rozdělí je na opravy (reaction) a údržbu (zbytek).
 * 
 * Logika:
 * - Reakce (reactionDuration) = čas od nahlášení do začátku opravy
 * - Zbytek (shutdownDuration - reactionDuration) = vlastní oprava/údržba
 * 
 * Hierarchie: PlanItem -> Workplace -> Location -> ParentLocation (rekurzivně)
 * 
 * Tento split je důležitý pro analýzu MTTR (Mean Time To Repair) a MTBF.
 * 
 * @param item PlanItem s shutdownDuration, reactionDuration, hourlyMaintenanceCosts
 * @returns {repairKc, maintenanceKc} - rozdělené náklady
 */
export function calcPlanItemMaintenanceSplitKc(item: any): { repairKc: number; maintenanceKc: number } {
    const minutes = Number(item?.shutdownDuration ?? 0) || 0;
    const reaction = Number(item?.reactionDuration ?? 0) || 0;
    const maintH = getHourlyMaintenanceCosts(item);
    
    // Náklady na reakci (čas čekání na technika)
    const repairKc = (maintH / 60) * reaction;
    
    // Náklady na vlastní opravu (zbývající čas)
    const rest = Math.max(0, minutes - reaction);
    const maintenanceKc = (maintH / 60) * rest;
    
    return { repairKc, maintenanceKc };
}

/**
 * Vypočítá náklady na zmetek (scrap).
 * 
 * Vzorec: počet zmetků * náklady na jeden kus
 * 
 * Náklady na kus se berou z:
 * 1. record.scrapCostsPerUnit (pokud je specificky nastaveno)
 * 2. record.product.scrapCosts (výchozí hodnota z produktu)
 * 
 * @param record ScrapRecord s scrapCount a scrapCostsPerUnit/product.scrapCosts
 * @returns Náklady v Kč
 */
export function calcScrapCostKc(record: any): number {
    const c = Number(record?.scrapCount ?? 0) || 0;
    const unit = Number(record?.scrapCostsPerUnit ?? record?.product?.scrapCosts ?? 0) || 0;
    return c * unit;
}


